import React, { useState, useEffect } from 'react';
import { Heart, Trash2, CheckCircle, Wand2, Bookmark, Palette, PenLine } from 'lucide-react';
import CompletedProjectModal from './CompletedProjectModal';
import CustomizationDetailModal from './CustomizationDetailModal';
import PhotoUploadModal from './PhotoUploadModal';
import LogoMark from './LogoMark';
import CuratorAdmin from './CuratorAdmin';

function MyProjectsDashboard({
  savedPatterns,
  patterns,
  setSelectedPattern,
  colors,
  fonts,
  user,
  supabase,
  setActiveView,
  setSavedPatterns,
  onAddPattern,
  stitches
}) {
  const [activeTab, setActiveTab] = useState('saved'); // 'saved', 'active', 'completed', 'designs', 'mypatterns'
  const [showSubmitPattern, setShowSubmitPattern] = useState(false);
  const [editingUserPattern, setEditingUserPattern] = useState(null);
  const [showStartModal, setShowStartModal] = useState(false);
  const [startingPattern, setStartingPattern] = useState(null);
  const [projectNotes, setProjectNotes] = useState('');
  const [customizations, setCustomizations] = useState([]);
  const [selectedCustomization, setSelectedCustomization] = useState(null);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [photoModalPattern, setPhotoModalPattern] = useState(null);
  const [projectPhotos, setProjectPhotos] = useState({}); // { savedPatternId: [photos] }
  const [selectedCompletedProject, setSelectedCompletedProject] = useState(null);

  // Load photos for all completed projects
  useEffect(() => {
    if (user) {
      loadAllPhotos();
    }
  }, [user]);

  const loadAllPhotos = async () => {
    const { data, error } = await supabase
      .from('project_photos')
      .select('*')
      .eq('user_id', user.id)
      .order('uploaded_at', { ascending: false });

    if (error) {
      console.error('Error loading photos:', error);
    } else {
      // Group photos by saved_pattern_id
      const grouped = {};
      data.forEach(photo => {
        if (!grouped[photo.saved_pattern_id]) {
          grouped[photo.saved_pattern_id] = [];
        }
        grouped[photo.saved_pattern_id].push(photo);
      });
      setProjectPhotos(grouped);
    }
  };

  // Load customizations on mount so tab count is accurate
  useEffect(() => {
    if (user) {
      loadCustomizations();
    }
  }, [user]);

  const loadCustomizations = async () => {
    const { data, error } = await supabase
      .from('pattern_customizations')
      .select('*')
      .eq('user_id', user.id);

    if (error) {
      console.error('Error loading customizations:', error);
    } else {
      setCustomizations(data || []);
    }
  };

  // Calculate counts for overview cards
  const counts = {
    saved: savedPatterns.filter(sp => sp.status === 'saved').length,
    inProgress: savedPatterns.filter(sp => sp.status === 'in_progress').length,
    completed: savedPatterns.filter(sp => sp.status === 'completed').length,
  };

  // Handle starting a project
  const handleStartProject = async (savedPattern) => {
    setStartingPattern(savedPattern);
    setShowStartModal(true);
  };

  const confirmStartProject = async () => {
    if (!startingPattern) return;

    const { error } = await supabase
      .from('saved_patterns')
      .update({
        status: 'in_progress',
        started_at: new Date().toISOString(),
        notes: projectNotes || null
      })
      .eq('id', startingPattern.id);

    if (error) {
      console.error('Error starting project:', error);
      alert('Failed to start project. Please try again.');
    } else {
      setShowStartModal(false);
      setStartingPattern(null);
      setProjectNotes('');
      // Force a page reload to refresh saved patterns
      window.location.reload();
    }
  };

  // Handle marking project as complete
  const handleMarkComplete = async (savedPattern) => {
  const { error } = await supabase
    .from('saved_patterns')
    .update({
      status: 'completed',
      completed_at: new Date().toISOString()
    })
    .eq('id', savedPattern.id);

  if (error) {
    console.error('Error completing project:', error);
    alert('Failed to mark complete. Please try again.');
  } else {
    // Increment complete_count
    await supabase.rpc('increment_complete_count', { pattern_id: savedPattern.pattern_id });

    const loadSavedPatterns = async () => {
      const { data } = await supabase
        .from('saved_patterns')
        .select('*')
        .eq('user_id', user.id);
      setSavedPatterns(data || []);
    };
    await loadSavedPatterns();
    setActiveTab('completed');

    // Prompt for photos
    setTimeout(() => {
      if (confirm('🎉 Project completed! Would you like to add photos of your finished work?')) {
        setPhotoModalPattern(savedPattern);
        setShowPhotoModal(true);
      }
    }, 500);
  }
  };

  const handleCancelProject = async (savedPattern) => {
    if (!window.confirm('Remove this from your active projects?')) return;
    const { error } = await supabase
      .from('saved_patterns')
      .delete()
      .eq('id', savedPattern.id);
    if (error) {
      console.error('Error cancelling project:', error);
      alert('Failed to remove project. Please try again.');
    } else {
      setSavedPatterns(savedPatterns.filter(p => p.id !== savedPattern.id));
    }
  };

  const handleDeleteCustomization = async (customization) => {
    if (!window.confirm('Delete this design? This can\'t be undone.')) return;
    const { error } = await supabase
      .from('pattern_customizations')
      .delete()
      .eq('id', customization.id);
    if (error) {
      console.error('Error deleting design:', error);
      alert('Failed to delete design. Please try again.');
    } else {
      setCustomizations(customizations.filter(c => c.id !== customization.id));
      setSelectedCustomization(null);
    }
  };

  // Filter patterns by tab
  const getFilteredPatterns = () => {
    switch (activeTab) {
      case 'active':
        return savedPatterns.filter(sp => sp.status === 'in_progress');
      case 'saved':
        return savedPatterns.filter(sp => sp.status === 'saved');
      case 'completed':
        return savedPatterns.filter(sp => sp.status === 'completed');
      case 'designs':
        return []; // We'll implement this later with pattern_customizations
      default:
        return savedPatterns;
    }
  };

  const filteredPatterns = getFilteredPatterns();

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{
          fontSize: '2rem',
          color: colors.charcoal,
          marginBottom: '0.5rem',
          fontFamily: fonts.display,
        }}>
          My Projects
        </h2>
        <p style={{ color: colors.darkGray, fontSize: '1.05rem' }}>
          Track your crochet journey from inspiration to completion
        </p>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '2rem',
        borderBottom: `2px solid ${colors.borderGreige}`,
        overflowX: 'auto'
      }}>
        {[
          { id: 'saved', label: `Saved (${counts.saved})`, Icon: Heart, color: '#c1795b' },
          { id: 'active', label: `In Progress (${counts.inProgress})`, Icon: Wand2, color: '#DDAC23' },
          { id: 'completed', label: `Completed (${counts.completed})`, Icon: CheckCircle, color: '#93A87A' },
          { id: 'designs', label: `My Designs (${customizations.length})`, Icon: Palette, color: '#8e8775' },
          { id: 'mypatterns', label: `My Patterns (${patterns.filter(p => p.submittedBy === user?.id).length})`, Icon: PenLine, color: '#8e8775' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              background: 'transparent',
              border: 'none',
              padding: '1rem 1.5rem',
              cursor: 'pointer',
              fontSize: '0.95rem',
              fontWeight: '600',
              color: activeTab === tab.id ? colors.charcoal : colors.brownGray,
              borderBottom: activeTab === tab.id ? `3px solid ${colors.dustyRust}` : '3px solid transparent',
              transition: 'all 0.2s',
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
            }}
          >
            <tab.Icon size={15} color={activeTab === tab.id ? tab.color : colors.brownGray} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content Area */}
      {activeTab === 'mypatterns' ? (
        // My Patterns Section
        (() => {
          const userPatterns = patterns.filter(p => p.submittedBy === user?.id);
          if (userPatterns.length === 0) {
            return (
              <div style={{ textAlign: 'center', padding: '4rem 2rem', background: colors.bgCream, borderRadius: '20px', border: `2px dashed ${colors.borderGreige}` }}>
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>✏️</div>
                <h3 style={{ fontSize: '1.5rem', color: colors.charcoal, marginBottom: '0.5rem', fontFamily: fonts.display }}>
                  No patterns submitted yet
                </h3>
                <p style={{ color: colors.darkGray, marginBottom: '2rem' }}>
                  Click "Submit a Pattern" to add your own patterns to your library!
                </p>
                {onAddPattern && (
                  <button
                    onClick={() => { setEditingUserPattern(null); setShowSubmitPattern(true); }}
                    style={{ padding: '0.75rem 2rem', background: `linear-gradient(135deg, ${colors.dustyRust} 0%, #a8674f 100%)`, color: '#fff', border: 'none', borderRadius: '50px', fontSize: '1rem', fontWeight: '600', cursor: 'pointer' }}
                  >
                    + Submit a Pattern
                  </button>
                )}
              </div>
            );
          }
          return (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '2rem' }}>
              {userPatterns.map(pattern => {
                const statusColor = pattern.status === 'published' ? colors.sageGreen : pattern.status === 'pending' ? colors.mustard : colors.brownGray;
                const statusLabel = pattern.status === 'published' ? 'Published' : pattern.status === 'pending' ? 'In Review' : 'Private';
                return (
                  <div key={pattern.id} style={{ background: '#fff', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 8px 25px rgba(0,0,0,0.12)', position: 'relative' }}>
                    {/* PenLine badge */}
                    <div style={{ position: 'absolute', top: '1rem', right: '1rem', background: '#8e8775', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(0,0,0,0.15)', zIndex: 10 }}>
                      <PenLine size={16} color="#fff" />
                    </div>
                    {/* Status Badge */}
                    <div style={{ position: 'absolute', top: '1rem', left: '1rem', background: statusColor, color: '#fff', padding: '0.35rem 0.75rem', borderRadius: '50px', fontSize: '0.8rem', fontWeight: '600', zIndex: 10 }}>
                      {statusLabel}
                    </div>
                    {/* Image */}
                    <div style={{ position: 'relative', paddingTop: '133%', background: `linear-gradient(135deg, ${colors.sageGreen} 0%, #8e9775 100%)` }}>
                      {pattern.image ? (
                        <img src={pattern.image} alt={pattern.title} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ opacity: 0.3, position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                          <LogoMark width={120} height={48} />
                        </div>
                      )}
                    </div>
                    {/* Info */}
                    <div style={{ padding: '1.25rem' }}>
                      <h3 style={{ fontSize: '1.1rem', fontWeight: '700', marginBottom: '0.5rem', color: colors.charcoal }}>{pattern.title}</h3>
                      {pattern.difficulty && <p style={{ fontSize: '0.85rem', color: colors.darkGray, marginBottom: '0.5rem' }}>{pattern.difficulty}</p>}
                      {pattern.sourceUrl && (
                        <a href={pattern.sourceUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.8rem', color: colors.sageGreen, fontWeight: '600', textDecoration: 'none' }}>
                          View Pattern Source ↗
                        </a>
                      )}
                    </div>
                    {/* Actions */}
                    <div style={{ padding: '0 1.25rem 1.25rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {onAddPattern && (
                        <button
                          onClick={() => { setEditingUserPattern(pattern); setShowSubmitPattern(true); }}
                          style={{ flex: 1, padding: '0.6rem', background: colors.bgCream, border: `1px solid ${colors.borderGreige}`, borderRadius: '8px', fontSize: '0.85rem', fontWeight: '600', color: colors.charcoal, cursor: 'pointer' }}
                        >
                          Edit
                        </button>
                      )}
                      {pattern.status === 'personal' && (
                        <button
                          onClick={async () => {
                            if (window.confirm('Submit this pattern for sharing? An admin will review it before it goes public.')) {
                              await supabase.from('patterns').update({ status: 'pending' }).eq('id', pattern.id);
                              window.location.reload();
                            }
                          }}
                          style={{ flex: 1, padding: '0.6rem', background: colors.mustard, border: 'none', borderRadius: '8px', fontSize: '0.85rem', fontWeight: '600', color: '#fff', cursor: 'pointer' }}
                        >
                          Submit for Sharing
                        </button>
                      )}
                      <button
                        onClick={async () => {
                          if (window.confirm(`Delete "${pattern.title}"? This can't be undone.`)) {
                            await supabase.from('patterns').delete().eq('id', pattern.id);
                            window.location.reload();
                          }
                        }}
                        style={{ padding: '0.6rem 0.75rem', background: 'transparent', border: `1px solid ${colors.borderGreige}`, borderRadius: '8px', fontSize: '0.85rem', color: colors.brownGray, cursor: 'pointer' }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()
      ) : activeTab === 'designs' ? (
        // Designs Section
        customizations.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '4rem 2rem',
            background: colors.bgCream,
            borderRadius: '20px',
            border: `2px dashed ${colors.borderGreige}`
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🎨</div>
            <h3 style={{
              fontSize: '1.5rem',
              color: colors.charcoal,
              marginBottom: '0.5rem',
              fontFamily: fonts.display
            }}>
              No designs saved yet
            </h3>
            <p style={{ color: colors.darkGray, marginBottom: '2rem' }}>
              Customize patterns in the Designer tool to save them here!
            </p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '2rem'
          }}>
            {customizations.map(customization => {
              const pattern = patterns.find(p => p.id === customization.pattern_id);
              const safeParse = (val, fallback) => {
                if (!val) return fallback;
                if (typeof val === 'object') return val;
                try { return JSON.parse(val); } catch { return fallback; }
              };
              const yarnColors = safeParse(customization.yarn_colors, {});
              const notes = safeParse(customization.notes, {});
              const displayTitle = pattern?.title || notes.title || 'Custom Design';
              const displayImage = pattern?.image || notes.imageUrl || null;

              return (
                <div
                  key={customization.id}
                  style={{
                    background: '#fff',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
                    transition: 'all 0.3s',
                    position: 'relative'
                  }}
                >
                  {/* Palette badge */}
                  <div style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'linear-gradient(135deg, #c1795b, #e09a7e)', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(0,0,0,0.15)', zIndex: 10 }}>
                    <Palette size={16} color="#fff" />
                  </div>

                  <div
                    onClick={() => setSelectedCustomization(customization)}
                    style={{ cursor: 'pointer' }}
                  >
                    {/* Pattern Image */}
                    <div style={{
                      position: 'relative',
                      paddingTop: '133%',
                      background: `linear-gradient(135deg, ${colors.sageGreen} 0%, #8e9775 100%)`
                    }}>
                      {displayImage ? (
                        <img
                          src={displayImage}
                          alt={displayTitle}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <div style={{
                          opacity: 0.3,
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)'
                        }}>
                          <LogoMark width={120} height={48} />
                        </div>
                      )}
                    </div>

                    {/* Pattern Info */}
                    <div style={{ padding: '1.25rem' }}>
                      <h3 style={{
                        fontSize: '1.1rem',
                        fontWeight: '700',
                        marginBottom: '0.5rem',
                        color: colors.charcoal
                      }}>
                        {displayTitle}
                      </h3>


                      {/* Color Swatches */}
                      {Object.keys(yarnColors).length > 0 && (
                        <div style={{
                          display: 'flex',
                          gap: '0.5rem',
                          marginTop: '0.75rem',
                          flexWrap: 'wrap'
                        }}>
                          {Object.values(yarnColors).slice(0, 5).map((color, idx) => (
                            <div
                              key={idx}
                              style={{
                                width: '30px',
                                height: '30px',
                                background: color.isCustom ? color.hex : colors.borderGreige,
                                borderRadius: '6px',
                                border: `2px solid ${colors.borderGreige}`,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                overflow: 'hidden'
                              }}
                              title={color.name}
                            >
                              {!color.isCustom && color.imageUrl && (
                                <img src={color.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              )}
                            </div>
                          ))}
                          {Object.keys(yarnColors).length > 5 && (
                            <div style={{
                              width: '30px',
                              height: '30px',
                              background: colors.bgCream,
                              borderRadius: '6px',
                              border: `2px solid ${colors.borderGreige}`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.7rem',
                              color: colors.darkGray,
                              fontWeight: '600'
                            }}>
                              +{Object.keys(yarnColors).length - 5}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Button */}
                  <div style={{
                    padding: '0 1.25rem 1.25rem'
                  }}>
                    <button
                      onClick={() => setSelectedCustomization(customization)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: colors.mustard,
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      View Design
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : filteredPatterns.length === 0 ? (
        // Empty State
        <div style={{
          textAlign: 'center',
          padding: '4rem 2rem',
          background: colors.bgCream,
          borderRadius: '20px',
          border: `2px dashed ${colors.borderGreige}`
        }}>
          <div style={{ marginBottom: '1rem' }}>
            {activeTab === 'active' ? <LogoMark width={160} height={64} /> : <span style={{ fontSize: '4rem' }}>{activeTab === 'saved' ? '🩷' : activeTab === 'completed' ? '✅' : '🎨'}</span>}

          </div>
          <h3 style={{
            fontSize: '1.5rem',
            color: colors.charcoal,
            marginBottom: '0.5rem',
            fontFamily: fonts.display
          }}>
            {activeTab === 'active' ? 'No active projects yet' :
             activeTab === 'saved' ? 'No saved patterns yet' :
             activeTab === 'completed' ? 'No completed projects yet' :
             'No designs saved yet'}
          </h3>
          <p style={{ color: colors.darkGray, marginBottom: '2rem' }}>
            {activeTab === 'active' ? 'Start working on a saved pattern to see it here!' :
             activeTab === 'saved' ? 'Browse patterns and click the bookmark icon to save your favorites!' :
             activeTab === 'completed' ? 'Mark projects as complete to see them here!' :
             'Customize patterns in the Designer tool to save them here!'}
          </p>
        </div>
      ) : (
        // Pattern Grid
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: '2rem'
        }}>
          {filteredPatterns.map(savedPattern => {
            const pattern = patterns.find(p => p.id === savedPattern.pattern_id);
            if (!pattern) return null;

            return (
              <div
                key={savedPattern.id}
                style={{
                  background: '#fff',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
                  transition: 'all 0.3s',
                  position: 'relative'
                }}
              >
                {/* Status Badge / Heart */}
                {savedPattern.status === 'saved' ? (
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      const { error } = await supabase.from('saved_patterns').delete().eq('id', savedPattern.id);
                      if (!error) setSavedPatterns(savedPatterns.filter(p => p.id !== savedPattern.id));
                    }}
                    title="Unsave"
                    style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'rgba(255,255,255,0.95)', border: `2px solid ${colors.dustyRust}`, borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: colors.dustyRust, zIndex: 10 }}
                  >
                    <Heart size={16} fill="currentColor" />
                  </button>
                ) : (
                  <div style={{
                    position: 'absolute',
                    top: '1rem',
                    right: '1rem',
                    background: savedPattern.status === 'in_progress' ? '#DDAC23' : '#93A87A',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                    zIndex: 10,
                  }}>
                    {savedPattern.status === 'in_progress'
                      ? <Wand2 size={16} color="#fff" />
                      : <CheckCircle size={18} color="#fff" />}
                  </div>
                )}

                <div
                  onClick={() => {
                    if (savedPattern.status === 'completed') {
                      setSelectedCompletedProject(savedPattern);
                    } else {
                      setSelectedPattern(pattern);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  {/* Pattern Image */}
                  <div style={{
                    position: 'relative',
                    paddingTop: '133%',
                    background: `linear-gradient(135deg, ${colors.sageGreen} 0%, #8e9775 100%)`
                  }}>
                    {/* Use user's photo if completed and has photos, otherwise pattern image */}
                    {savedPattern.status === 'completed' && projectPhotos[savedPattern.id]?.[0] ? (
                      <img
                        src={projectPhotos[savedPattern.id][0].photo_url}
                        alt={pattern.title}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : pattern.image ? (
                      <img
                        src={pattern.image}
                        alt={pattern.title}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <div style={{
                        opacity: 0.3,
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)'
                      }}>
                        <LogoMark width={120} height={48} />
                      </div>
                    )}
                  </div>

                  {/* Pattern Info */}
                  <div style={{ padding: '1.25rem' }}>
                    <h3 style={{
                      fontSize: '1.1rem',
                      fontWeight: '700',
                      marginBottom: '0.5rem',
                      color: colors.charcoal
                    }}>
                      {pattern.title}
                    </h3>

                    {savedPattern.started_at && (
                      <p style={{
                        color: colors.brownGray,
                        fontSize: '0.85rem',
                        marginBottom: '0.5rem'
                      }}>
                        Started: {new Date(savedPattern.started_at).toLocaleDateString()}
                      </p>
                    )}

                    {savedPattern.completed_at && (
                      <p style={{
                        color: colors.brownGray,
                        fontSize: '0.85rem',
                        marginBottom: '0.5rem'
                      }}>
                        Completed: {new Date(savedPattern.completed_at).toLocaleDateString()}
                      </p>
                    )}

                    {/* Only show badges on Saved tab */}
                    {savedPattern.status === 'saved' && (
                      <div
                        style={{
                          display: 'flex',
                          gap: '0.5rem',
                          flexWrap: 'wrap',
                          marginTop: '0.75rem'
                        }}>
                        <span style={{
                          background: colors.bgCream,
                          padding: '0.35rem 0.75rem',
                          borderRadius: '50px',
                          fontSize: '0.8rem',
                          color: colors.darkGray
                        }}>
                          {pattern.difficulty}
                        </span>
                        <span style={{
                          background: colors.bgCream,
                          padding: '0.35rem 0.75rem',
                          borderRadius: '50px',
                          fontSize: '0.8rem',
                          color: colors.darkGray
                        }}>
                          {pattern.yarnWeight}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{
                  padding: '0 1.25rem 1.25rem',
                  display: 'flex',
                  gap: '0.75rem'
                }}>
                  {savedPattern.status === 'saved' && (
                    <button
                      onClick={() => handleStartProject(savedPattern)}
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        background: colors.dustyRust,
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Start Project
                    </button>
                  )}

                  {savedPattern.status === 'in_progress' && (
                    <>
                      <button
                        onClick={() => handleMarkComplete(savedPattern)}
                        style={{
                          flex: 1,
                          padding: '0.75rem',
                          background: colors.mustard,
                          color: '#fff',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        Mark Complete
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleCancelProject(savedPattern); }}
                        style={{
                          padding: '0.75rem',
                          background: 'transparent',
                          color: colors.brownGray,
                          border: `1px solid ${colors.borderGreige}`,
                          borderRadius: '8px',
                          fontSize: '0.85rem',
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                    </>
                  )}

                  {savedPattern.status === 'completed' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (projectPhotos[savedPattern.id]?.length > 0) {
                          setSelectedCompletedProject(savedPattern);
                        } else {
                          setPhotoModalPattern(savedPattern);
                          setShowPhotoModal(true);
                        }
                      }}
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        background: colors.sageGreen,
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      {projectPhotos[savedPattern.id]?.length > 0
                        ? `📸 Photos (${projectPhotos[savedPattern.id].length})`
                        : '📸 Add Photos'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Start Project Modal */}
      {showStartModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '2rem'
        }}
        onClick={() => setShowStartModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: '20px',
              maxWidth: '500px',
              width: '100%',
              padding: '2rem',
              fontFamily: fonts.body
            }}
          >
            <h3 style={{
              fontSize: '1.5rem',
              fontFamily: fonts.display,
              color: colors.charcoal,
              marginBottom: '1rem'
            }}>
              Start This Project
            </h3>

            <p style={{
              color: colors.darkGray,
              marginBottom: '1.5rem'
            }}>
              Add any notes about yarn, colors, or modifications you're planning:
            </p>

            <textarea
              value={projectNotes}
              onChange={(e) => setProjectNotes(e.target.value)}
              placeholder="e.g., Using mustard and cream yarn, making it bigger by adding 20 rows..."
              style={{
                width: '100%',
                minHeight: '120px',
                padding: '1rem',
                border: `2px solid ${colors.borderGreige}`,
                borderRadius: '8px',
                fontSize: '0.95rem',
                fontFamily: fonts.body,
                resize: 'vertical',
                marginBottom: '1.5rem',
                boxSizing: 'border-box'
              }}
            />

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={() => {
                  setShowStartModal(false);
                  setProjectNotes('');
                }}
                style={{
                  flex: 1,
                  padding: '0.875rem',
                  background: colors.borderGreige,
                  color: colors.charcoal,
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmStartProject}
                style={{
                  flex: 1,
                  padding: '0.875rem',
                  background: colors.dustyRust,
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Start Project!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Customization Detail Modal */}
      {selectedCustomization && (
        <CustomizationDetailModal
          customization={selectedCustomization}
          pattern={patterns.find(p => p.id === selectedCustomization.pattern_id)}
          colors={colors}
          fonts={fonts}
          user={user}
          supabase={supabase}
          onClose={() => setSelectedCustomization(null)}
          onDelete={handleDeleteCustomization}
          onContinueEditing={(customization) => {
            // Switch to Designer view and pass the customization
            setActiveView('designer');
            // Store customization in a way Designer can access it
            // This is a placeholder - you'll need to wire this up in your main App.js
            window.loadedCustomization = customization;
          }}
        />
      )}

      {/* Photo Upload Modal */}
      {showPhotoModal && photoModalPattern && (
        <PhotoUploadModal
          savedPattern={photoModalPattern}
          pattern={patterns.find(p => p.id === photoModalPattern.pattern_id)}
          colors={colors}
          fonts={fonts}
          user={user}
          supabase={supabase}
          onClose={() => {
            setShowPhotoModal(false);
            setPhotoModalPattern(null);
          }}
          onPhotosUploaded={(photos) => {
            // Reload all photos
            loadAllPhotos();
          }}
        />
      )}

      {/* Completed Project Modal */}
      {selectedCompletedProject && (
        <CompletedProjectModal
          savedPattern={selectedCompletedProject}
          pattern={patterns.find(p => p.id === selectedCompletedProject.pattern_id)}
          photos={projectPhotos[selectedCompletedProject.id] || []}
          colors={colors}
          fonts={fonts}
          user={user}
          supabase={supabase}
          onClose={() => setSelectedCompletedProject(null)}
          onPhotosUpdated={() => {
            loadAllPhotos();
          }}
        />
      )}

      {/* Submit / Edit Pattern Overlay */}
      {showSubmitPattern && onAddPattern && stitches && (
        <CuratorAdmin
          userMode={true}
          currentUser={user}
          onClose={() => { setShowSubmitPattern(false); setEditingUserPattern(null); }}
          onAddPattern={async (patternData) => {
            const savedId = await onAddPattern(patternData);
            setShowSubmitPattern(false);
            setEditingUserPattern(null);

            if (patternData.status === 'personal' && savedId) {
              const alsoShare = window.confirm(
                "Pattern saved to your library!\n\nWould you also like to submit it for community sharing?\n\n• Your pattern will be reviewed before sharing\n• Our team may make minor edits\n• Submissions that don't meet guidelines may not be published"
              );
              if (alsoShare) {
                await supabase.from('patterns').update({ status: 'pending' }).eq('id', savedId);
                await onAddPattern({ ...patternData, id: savedId, status: 'pending' });
                alert("Submitted for review! Check your My Patterns tab.");
              }
            } else if (patternData.status === 'pending') {
              alert("Submitted for review! It's saved in your My Patterns tab as 'In Review'.");
            }
          }}
          stitches={stitches}
          editingPatternId={editingUserPattern?.id || null}
          initialSpecs={editingUserPattern ? {
            difficulty: editingUserPattern.difficulty,
            category: editingUserPattern.category || [],
            yarnWeight: editingUserPattern.yarnWeight,
            yarnMaterial: editingUserPattern.yarnMaterial,
            hook: editingUserPattern.hook || [],
            stitches: editingUserPattern.stitches || [],
            specialStitches: editingUserPattern.specialStitches || '',
            gaugeStitches: editingUserPattern.gauge?.stitches?.toString() || '',
            gaugeRows: editingUserPattern.gauge?.rows?.toString() || '',
            yarnYards: editingUserPattern.totalYards?.toString() || '',
            originalWidth: editingUserPattern.originalSize?.width?.toString() || '',
            originalHeight: editingUserPattern.originalSize?.height?.toString() || '',
            videoUrl: editingUserPattern.videoUrl || '',
            videoCreator: editingUserPattern.videoCreator || '',
            curatorNotes: '',
            customAttribution: editingUserPattern.siteName || '',
            customImage: editingUserPattern.image || '',
          } : null}
          initialYarnColors={editingUserPattern?.yarnColors || []}
          initialPreview={editingUserPattern ? {
            title: editingUserPattern.title,
            description: editingUserPattern.description,
            image: editingUserPattern.image,
            sourceUrl: editingUserPattern.sourceUrl,
            siteName: editingUserPattern.siteName || editingUserPattern.creator,
          } : null}
          initialStep={editingUserPattern ? 'preview' : 'url'}
          onResetEdit={() => setEditingUserPattern(null)}
        />
      )}
    </div>
  );
}

export default MyProjectsDashboard;
